import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useApiSend, getApiErrorMessage } from '../../utils/httpClientTs';
import {
  createFundNamedAmount,
  patchFundNamedAmount,
  deleteFundNamedAmount,
} from '../../endpoints/financeFunds';
import type {
  FundNamedAmountDTO,
  FundTypeOption,
} from '../../types/financeFunds';
import AlertItem from '../../Components/AlertItem';

interface FundNamedAmountsSectionProps {
  fundId: string;
  categoryId: string;
  namedAmounts: FundNamedAmountDTO[];
  typeOptions: FundTypeOption[];
  canManage: boolean;
}

type FormState = {
  amount: string;
  typeId: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

const emptyForm = (): FormState => ({
  amount: '',
  typeId: '',
  startDate: null,
  endDate: null,
});

const formatAmount = (amount: number) =>
  amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const FundNamedAmountsSection: React.FC<FundNamedAmountsSectionProps> = ({
  fundId,
  categoryId,
  namedAmounts,
  typeOptions,
  canManage,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const filteredTypes = useMemo(
    () =>
      typeOptions.filter((t) => !categoryId || t.category_id === categoryId),
    [typeOptions, categoryId]
  );

  const invalidateKeys = [['fund-detail', fundId]];

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setActionError(null);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setActionError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: FundNamedAmountDTO) => {
    setEditingId(row.id);
    setForm({
      amount: String(row.amount),
      typeId: row.type_id,
      startDate: row.start_date ? dayjs(row.start_date) : null,
      endDate: row.end_date ? dayjs(row.end_date) : null,
    });
    setActionError(null);
    setDialogOpen(true);
  };

  const validateForm = (): string | null => {
    const amount = Number(form.amount);
    if (!form.typeId) return 'Fund type is required';
    if (!form.startDate) return 'Start date is required';
    if (!Number.isFinite(amount) || amount <= 0) {
      return 'Amount must be greater than zero';
    }
    if (
      form.endDate &&
      form.startDate &&
      form.endDate.isBefore(form.startDate, 'day')
    ) {
      return 'End date cannot be before start date';
    }
    return null;
  };

  const buildPayload = () => {
    const amount = Number(form.amount);
    const start_date = form.startDate!.format('YYYY-MM-DD');
    const end_date = form.endDate ? form.endDate.format('YYYY-MM-DD') : null;
    return { amount, type_id: form.typeId, start_date, end_date };
  };

  const { mutateAsync: createMut, isPending: creating } = useApiSend(
    (body: ReturnType<typeof buildPayload>) =>
      createFundNamedAmount(fundId, body),
    closeDialog,
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const { mutateAsync: patchMut, isPending: patching } = useApiSend(
    (p: { id: string; body: ReturnType<typeof buildPayload> }) =>
      patchFundNamedAmount(fundId, p.id, p.body),
    closeDialog,
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const { mutateAsync: deleteMut, isPending: deleting } = useApiSend(
    (id: string) => deleteFundNamedAmount(fundId, id),
    () => setDeleteId(null),
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const handleSave = async () => {
    const err = validateForm();
    if (err) {
      setActionError(err);
      return;
    }
    const body = buildPayload();
    if (editingId) {
      await patchMut({ id: editingId, body });
    } else {
      await createMut(body);
    }
  };

  return (
    <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
      <CardHeader
        title="Named amounts"
        action={
          canManage ? (
            <Button startIcon={<Add />} onClick={openCreate} size="small">
              Add
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {actionError && !dialogOpen && (
          <Box sx={{ mb: 2 }}>
            <AlertItem
              severity="error"
              description={actionError}
              onClose={() => setActionError(null)}
              keepOpen
            />
          </Box>
        )}
        {namedAmounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No named amounts defined for this primary fund.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                {canManage && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {namedAmounts.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.type_name || row.type_id}</TableCell>
                  <TableCell align="right">
                    ${formatAmount(row.amount)}
                  </TableCell>
                  <TableCell>{row.start_date}</TableCell>
                  <TableCell>{row.end_date || '—'}</TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label="Edit named amount"
                        onClick={() => openEdit(row)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete named amount"
                        onClick={() => setDeleteId(row.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit named amount' : 'Add named amount'}
        </DialogTitle>
        <DialogContent>
          {actionError && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <AlertItem
                severity="error"
                description={actionError}
                onClose={() => setActionError(null)}
                keepOpen
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Fund type</InputLabel>
              <Select
                label="Fund type"
                value={form.typeId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, typeId: String(e.target.value) }))
                }
              >
                {filteredTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Amount"
              type="number"
              size="small"
              fullWidth
              required
              inputProps={{ min: 0.01, step: '0.01' }}
              value={form.amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, amount: e.target.value }))
              }
            />
            <DatePicker
              label="Start date"
              value={form.startDate}
              onChange={(v) => setForm((f) => ({ ...f, startDate: v }))}
              slotProps={{
                textField: { fullWidth: true, size: 'small', required: true },
              }}
            />
            <DatePicker
              label="End date"
              value={form.endDate}
              onChange={(v) => setForm((f) => ({ ...f, endDate: v }))}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={creating || patching}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete named amount?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={() => {
              if (deleteId) void deleteMut(deleteId);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FundNamedAmountsSection;
